import type { AsyncThunk, ListenerMiddlewareInstance, UnknownAction } from '@reduxjs/toolkit';
import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';

import { FetchErrorName, ForbiddenErrors } from '../../api/errors/types';
import { Curtain } from '../../components/curtain/Curtain';
import { PopupType } from '../../components/popup/types';
import { Telemetry } from '../../telemetry/Telemetry';
import { fetchAssignedTablesAction } from '../apiActions/fetchAssignedTablesAction';
import { fetchCasinoConfigAction } from '../apiActions/fetchCasinoConfigAction';
import { fetchEnvAction } from '../apiActions/fetchEnvAction';
import { rejectHistoryFetchAction } from '../apiActions/fetchHistoryAction';
import { fetchLiveSupportMessagesAction } from '../apiActions/fetchLiveSupportMessagesAction';
import { fetchPlayerConfigAction } from '../apiActions/fetchPlayerConfigAction';
import { fetchPlayerConfigSyncAction } from '../apiActions/fetchPlayerConfigSyncAction';
import { fetchRtpAction } from '../apiActions/fetchRtpAction';
import { fetchStatsConfigAction } from '../apiActions/fetchStatsConfigAction';
import { fetchStatsSettingsAction } from '../apiActions/fetchStatsSettingsAction';
import { fetchTableConfigAction } from '../apiActions/fetchTableConfigAction';
import { fetchTableLevelMessagesAction } from '../apiActions/fetchTableLevelMessagesAction';
import { fetchUserInfoAction } from '../apiActions/fetchUserInfoAction';
import { fetchVersionsAction } from '../apiActions/fetchVersionsAction';
import type { CoreDispatch, CoreRootState } from '../CoreStore';
import { gameStateActions } from '../slices/game/slice';
import { initializationActions } from '../slices/initialization/slice';
import { popupActions } from '../slices/popup/slice';

const defaultErrorActions = [
  rejectHistoryFetchAction.rejected,
  fetchAssignedTablesAction.rejected,
  fetchCasinoConfigAction.rejected,
  fetchEnvAction.rejected,
  fetchLiveSupportMessagesAction.rejected,
  fetchPlayerConfigAction.rejected,
  fetchPlayerConfigSyncAction.rejected,
  fetchRtpAction.rejected,
  fetchStatsConfigAction.rejected,
  fetchStatsSettingsAction.rejected,
  fetchTableConfigAction.rejected,
  fetchTableLevelMessagesAction.rejected,
  fetchUserInfoAction.rejected,
  fetchVersionsAction.rejected,
];

type RejectedAction = AsyncThunk<unknown, any, any>['rejected'];
interface ErrorDetails {
  name: string;
  message: string;
}
export function getErrorDetails(error: unknown): ErrorDetails {
  const err = error as ErrorDetails | null;
  return {
    name: err?.name ?? '',
    message: err?.message ?? '',
  };
}

export const handleErrorAction = (action: UnknownAction, state: CoreRootState, dispatch: CoreDispatch) => {
  if (!('popup' in state)) {
    console.error('Store does not have a popup slice.');
  }
  const sessionExpirationPopupVisible = state.popup?.currentPopup?.popupName === PopupType.SessionExpired;
  const sessionTerminationPopupVisible = state.popup?.currentPopup?.popupName === PopupType.SessionTerminated;

  // If the request was essential, we should show a popup to the user
  // We should not needlessly dispatch the popup if it is already visible
  // Session termination popup takes precedence over all other generic error popups
  // Any session expiration error should be critical and display the blocking popup
  const { name: errorName, message: errorMessage } = getErrorDetails(action.error);
  switch (errorName) {
    case FetchErrorName.UnauthorizedError:
      Telemetry.reportFailure({ name: 'session', payload: [action.type, 'fetchErrorMiddleware'] }, { once: true });

      if (!sessionExpirationPopupVisible && !sessionTerminationPopupVisible) {
        Curtain.resurrect();
        dispatch(popupActions.overwritePopup(PopupType.SessionExpired));
        dispatch(gameStateActions.setGameAbandoned(true));
      }
      dispatch(initializationActions.setIsReadyToRender(false));
      break;

    case FetchErrorName.ForbiddenError:
      if (errorMessage === ForbiddenErrors.RESTRICT_USER && !sessionTerminationPopupVisible) {
        Curtain.resurrect();
        dispatch(popupActions.overwritePopup(PopupType.SessionTerminated));
        dispatch(gameStateActions.setGameAbandoned(true));
      }
      // TODO: redirection is happening in game initializer. Refactor me
      Telemetry.reportFailure({ name: 'forbidden', payload: [action.type, 'fetchErrorMiddleware'] }, { once: true });
      break;

    default:
      // TODO: Probably we need to log all critical errors here
      if (
        // Making typescript happy
        typeof action.meta === 'object' &&
        action.meta !== null &&
        'arg' in action.meta &&
        typeof action.meta?.arg === 'object' &&
        action.meta.arg !== null &&
        'essential' in action.meta.arg &&
        action.meta.arg.essential &&
        !sessionExpirationPopupVisible
      ) {
        dispatch(popupActions.pushPopup(PopupType.GenericError));
      }
      break;
  }
};

export interface ICreateFetchErrorMiddlewareOptions<ActionType extends RejectedAction> {
  additionalActions?: ActionType[];
}

export const createFetchErrorMiddleware = <ActionType extends RejectedAction>(
  options?: ICreateFetchErrorMiddlewareOptions<ActionType>
): ListenerMiddlewareInstance => {
  const fetchErrorMiddleware = createListenerMiddleware();

  const actions = options?.additionalActions
    ? [...defaultErrorActions, ...options.additionalActions]
    : defaultErrorActions;
  const startListening = fetchErrorMiddleware.startListening.withTypes<CoreRootState, CoreDispatch>();

  startListening({
    matcher: isAnyOf(...actions),
    effect: (action, { dispatch, getState }) => {
      handleErrorAction(action, getState(), dispatch);
    },
  });

  return fetchErrorMiddleware;
};
